import { useMemo } from 'react';
import { Badge, Card, Form, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'src/utils';
import type {
	ColorOption,
	MotornetAccessories,
	MotornetVehicleKind,
	OptionalCatalogItem,
} from '../types';
import {
	validateMotornetVehicleOptions,
	type MotornetAccessoriesResponse,
} from '../common/MotornetAccessoriesValidator';

interface MotornetOptionalsPickerProps {
	catalog: OptionalCatalogItem[];
	value: OptionalCatalogItem[];
	onChange: (next: OptionalCatalogItem[]) => void;
	accessories?: MotornetAccessories;
	eurotaxCode: string;
	vehicleKind: MotornetVehicleKind;
	exteriorColor?: ColorOption | null;
	interiorColor?: ColorOption | null;
}

type OptionRuleState = 'included' | 'excluded' | 'constrained';

const RULE_META: Record<
	OptionRuleState,
	{ bg: string; accent: string; key: string; fallback: string }
> = {
	included: {
		bg: 'primary',
		accent: 'var(--bs-primary, #0d6efd)',
		key: 'motornet_opt_included',
		fallback: 'Incluso',
	},
	excluded: {
		bg: 'danger',
		accent: 'var(--bs-danger, #dc3545)',
		key: 'motornet_opt_excluded',
		fallback: 'Escluso',
	},
	constrained: {
		bg: 'success',
		accent: 'var(--bs-success, #198754)',
		key: 'motornet_opt_constrained',
		fallback: 'Vincolato',
	},
};

const subtitleOf = (item: OptionalCatalogItem): string => {
	const parts = [item.descrizioneEquipaggiamento, item.descrizioneNormalizzata]
		.filter(Boolean)
		.filter((p) => p !== item.descrizione);
	return [...new Set(parts)].join(' · ');
};

const MotornetOptionalsPicker = ({
	catalog,
	value,
	onChange,
	accessories,
	eurotaxCode,
	vehicleKind,
	exteriorColor,
	interiorColor,
}: MotornetOptionalsPickerProps) => {
	const { t } = useTranslation();
	const selected = new Set(value.map((o) => o.idAccessorio));

	const validation = useMemo(
		() =>
			validateMotornetVehicleOptions({
				eurotaxCode,
				vehicleKind,
				accessories: (accessories ?? {}) as unknown as MotornetAccessoriesResponse,
				selectedOptionIds: [
					...value.map((o) => o.idDizionario),
					exteriorColor?.isStandard ? null : exteriorColor?.idDizionario,
					interiorColor?.isStandard ? null : interiorColor?.idDizionario,
				].filter((id): id is number => id != null),
			}),
		[eurotaxCode, vehicleKind, accessories, value, exteriorColor, interiorColor]
	);

	const stateByDizionario = useMemo(() => {
		const map = new Map<number, OptionRuleState>();
		for (const id of validation.includedOptionIds) map.set(id, 'included');
		for (const id of validation.constrainedOptionIds) map.set(id, 'constrained');
		for (const id of validation.excludedOptionIds) map.set(id, 'excluded');
		return map;
	}, [validation]);

	const toggle = (item: OptionalCatalogItem, checked: boolean) => {
		if (checked) {
			onChange([...value, item]);
		} else {
			onChange(value.filter((o) => o.idAccessorio !== item.idAccessorio));
		}
	};

	const catalogIds = new Set(catalog.map((i) => i.idAccessorio));

	const allSelected =
		catalog.length > 0 && catalog.every((i) => selected.has(i.idAccessorio));
	const someSelected = catalog.some((i) => selected.has(i.idAccessorio));

	const toggleAll = (checked: boolean) => {
		const kept = value.filter((o) => !catalogIds.has(o.idAccessorio));
		onChange(checked ? [...kept, ...catalog] : kept);
	};

	if (!catalog.length) {
		return (
			<Card className="mt-2">
				<Card.Body className="text-muted">
					{t('motornet_no_optionals', 'Nessun optional disponibile')}
				</Card.Body>
			</Card>
		);
	}

	const legend: OptionRuleState[] = ['included', 'constrained', 'excluded'];

	return (
		<Card className="mt-2">
			<Card.Body>
				<div className="d-flex gap-3 flex-wrap mb-2 small text-muted">
					{legend.map((state) => (
						<span key={state} className="d-inline-flex align-items-center gap-1">
							<span
								style={{
									width: '0.75rem',
									height: '0.75rem',
									borderRadius: '2px',
									background: RULE_META[state].accent,
									display: 'inline-block',
								}}
							/>
							{t(RULE_META[state].key, RULE_META[state].fallback)}
						</span>
					))}
				</div>
				<Table responsive hover className="align-middle mb-0">
					<thead>
						<tr>
							<th style={{ width: '3rem' }}>
								<Form.Check
									type="checkbox"
									id="mot-opt-all"
									checked={allSelected}
									ref={(el: HTMLInputElement | null) => {
										if (el) el.indeterminate = someSelected && !allSelected;
									}}
									onChange={(e) => toggleAll(e.target.checked)}
									aria-label={t('select_all', 'Seleziona tutti')}
								/>
							</th>
							<th style={{ width: '8rem' }}>{t('code', 'Codice')}</th>
							<th>{t('description', 'Descrizione')}</th>
							<th className="text-end" style={{ width: '10rem' }}>
								{t('price', 'Prezzo')}
							</th>
						</tr>
					</thead>
					<tbody>
						{catalog.map((item) => {
							const isChecked = selected.has(item.idAccessorio);
							const subtitle = subtitleOf(item);
							const ruleState =
								item.idDizionario != null
									? stateByDizionario.get(item.idDizionario)
									: undefined;
							const meta = ruleState ? RULE_META[ruleState] : null;
							const isLocked = ruleState === 'excluded'
							return (
								<tr
									key={item.idAccessorio}
									style={{
										cursor: isLocked ? 'default' : 'pointer',
										boxShadow: meta ? `inset 4px 0 0 ${meta.accent}` : undefined,
									}}
									onClick={
										isLocked ? undefined : () => toggle(item, !isChecked)
									}
								>
									<td onClick={(e) => e.stopPropagation()}>
										<Form.Check
											type="checkbox"
											id={`mot-opt-${item.idAccessorio}`}
											checked={isChecked}
											disabled={isLocked}
											onChange={(e) => toggle(item, e.target.checked)}
											aria-label={item.descrizione || item.codice}
										/>
									</td>
									<td>{item.codice}</td>
									<td>
										<div className="d-flex align-items-center gap-2">
											<span>{item.descrizione || item.descrizioneNormalizzata}</span>
											{meta && (
												<Badge bg={meta.bg} pill>
													{t(meta.key, meta.fallback)}
												</Badge>
											)}
										</div>
										{subtitle && (
											<small className="text-muted d-block">{subtitle}</small>
										)}
										{item.descrizioneBreve && (
											<small className="text-muted d-block">
												{item.descrizioneBreve}
											</small>
										)}
									</td>
									<td className="text-end">
										{item.prezzo != null ? formatCurrency(item.prezzo) : '—'}
									</td>
								</tr>
							);
						})}
					</tbody>
				</Table>
			</Card.Body>
		</Card>
	);
};

export default MotornetOptionalsPicker;
